/**
 * UI 模块设置（独立页面，原断言复核页「模块设置」弹窗迁移）
 * 样式对齐系统配置其它页面：page-header + content-card + Card 分区
 * - 共识断言：开关 / 分歧策略 / 置信度阈值 / 副模型配置
 * - 视频断言：全程视频录制 + 过程帧多模态判定开关
 * - 一键提单：禅道 / Jira 平台配置（Token 脱敏回显，留空保持不变）
 * - 邮箱验证码：IMAP 测试邮箱配置（密码脱敏回显，留空保持不变）
 */
import { useEffect, useState } from 'react'
import { Button, Card, Form, Input, InputNumber, Radio, Select, Switch, Tag, Typography, message } from 'antd'
import { BellOutlined, ExperimentOutlined, FileDoneOutlined, MailOutlined, VideoCameraOutlined } from '@ant-design/icons'
import * as uiApi from '@/api/uiAutomation'
import type { UISettings } from '@/types'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 分区卡片标题（图标 + 名称 + 说明标签） */
function SectionTitle({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 15, color: 'var(--au-primary)', display: 'inline-flex' }}>{icon}</span>
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
      {hint && <Tag style={{ marginRight: 0 }}>{hint}</Tag>}
    </div>
  )
}

export default function UIModuleSettings() {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [apiKeySet, setApiKeySet] = useState(false)
  // 三期 E：IMAP 密码是否已配置（脱敏回显，留空保持不变）
  const [imapPasswordSet, setImapPasswordSet] = useState(false)
  // 四期 L：提单平台 Token 是否已配置（脱敏回显，留空保持不变）
  const [ticketTokenSet, setTicketTokenSet] = useState(false)
  // 五期 #4：SMTP 密码是否已配置（脱敏回显，留空保持不变）
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const s: UISettings = await uiApi.getUISettings()
        setApiKeySet(s.secondary_api_key_set)
        setImapPasswordSet(s.imap_password_set)
        setTicketTokenSet(s.ticket_token_set)
        form.setFieldsValue({
          consensus_enabled: s.consensus_enabled,
          consensus_policy: s.consensus_policy,
          confidence_threshold: s.confidence_threshold,
          secondary_provider: s.secondary_provider,
          secondary_base_url: s.secondary_base_url,
          secondary_model_name: s.secondary_model_name,
          imap_host: s.imap_host,
          imap_port: s.imap_port,
          imap_ssl: s.imap_ssl,
          imap_user: s.imap_user,
          imap_password: '',
          // 四期 C：视频断言开关
          video_assertions_enabled: s.video_assertions_enabled,
          // 四期 L：一键提单配置（token 不回传明文）
          ticket_provider: s.ticket_provider || '',
          ticket_base_url: s.ticket_base_url,
          ticket_token: '',
          ticket_project_key: s.ticket_project_key,
          // 五期 #4：定时执行通知（SMTP 密码不回传明文）
          smtp_host: s.smtp_host,
          smtp_port: s.smtp_port ?? 465,
          smtp_ssl: s.smtp_ssl,
          smtp_user: s.smtp_user,
          smtp_password: '',
          notify_recipients: s.notify_recipients,
          notify_on_success: s.notify_on_success,
          site_url: s.site_url,
        })
      } catch {
        // 加载失败保持表单空值
      }
    })()
  }, [form])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)
      const saved = await uiApi.updateUISettings({
        consensus_enabled: values.consensus_enabled,
        consensus_policy: values.consensus_policy,
        confidence_threshold: values.confidence_threshold,
        secondary_provider: values.secondary_provider,
        secondary_base_url: values.secondary_base_url || '',
        secondary_api_key: values.secondary_api_key || '',
        secondary_model_name: values.secondary_model_name || '',
        // 三期 E：邮箱验证码 IMAP 配置（密码留空=保持不变）
        imap_host: values.imap_host || '',
        imap_port: values.imap_port ?? 993,
        imap_ssl: !!values.imap_ssl,
        imap_user: values.imap_user || '',
        imap_password: values.imap_password || '',
        // 四期 C：视频断言开关
        video_assertions_enabled: !!values.video_assertions_enabled,
        // 四期 L：一键提单配置（token 留空=保持不变）
        ticket_provider: values.ticket_provider || '',
        ticket_base_url: values.ticket_base_url || '',
        ticket_token: values.ticket_token || '',
        ticket_project_key: values.ticket_project_key || '',
        // 五期 #4：定时执行通知配置（密码留空=保持不变）
        smtp_host: values.smtp_host || '',
        smtp_port: values.smtp_port ?? 465,
        smtp_ssl: !!values.smtp_ssl,
        smtp_user: values.smtp_user || '',
        smtp_password: values.smtp_password || '',
        notify_recipients: values.notify_recipients || '',
        notify_on_success: !!values.notify_on_success,
        site_url: values.site_url || '',
      })
      setApiKeySet(saved.secondary_api_key_set)
      setImapPasswordSet(saved.imap_password_set)
      setTicketTokenSet(saved.ticket_token_set)
      setSmtpPasswordSet(saved.smtp_password_set)
      message.success('设置已保存')
    } catch (err) {
      console.error('[UIModuleSettings] 保存设置失败:', err)
    } finally {
      setSaving(false)
    }
  }

  const cardStyle = { borderRadius: 10, border: '1px solid var(--au-border)' } as const
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' } as const

  return (
    <div className="aurora-page">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          UI 模块设置
        </Title>
        <Button type="primary" loading={saving} onClick={handleSave}>
          保存设置
        </Button>
      </div>

      <div className="content-card" style={{ padding: 20, maxWidth: 880 }}>
        <Form form={form} layout="vertical">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ============ 双模型共识断言 ============ */}
            <Card size="small" style={cardStyle} title={<SectionTitle icon={<ExperimentOutlined />} title="双模型共识断言" hint="断言复核联动" />}>
              <div style={grid2}>
                <Form.Item
                  name="consensus_enabled"
                  label="启用共识断言"
                  valuePropName="checked"
                  extra="主副两个模型独立判定，两票一致取平均置信度；分歧按策略处理并自动入复核队列"
                >
                  <Switch checkedChildren="开" unCheckedChildren="关" />
                </Form.Item>
                <Form.Item
                  name="confidence_threshold"
                  label="置信度阈值"
                  extra="低于该值自动进入复核队列（0 表示关闭低置信度入队）"
                >
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </div>
              <Form.Item name="consensus_policy" label="分歧策略" extra="主副模型判定结果不一致时的处理方式">
                <Radio.Group
                  options={[
                    { value: 'fail-on-disagreement', label: '分歧判失败（保守）' },
                    { value: 'pass-on-disagreement', label: '分歧判通过（宽松）' },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <div style={grid2}>
                <Form.Item name="secondary_provider" label="副模型提供商">
                  <Select
                    options={[
                      { value: 'qwen', label: '通义千问 Qwen' },
                      { value: 'deepseek', label: 'DeepSeek' },
                      { value: 'openai', label: 'OpenAI' },
                      { value: 'custom', label: '自定义（OpenAI 兼容）' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="secondary_model_name" label="副模型名称">
                  <Input placeholder="如 qwen-plus（留空按提供商默认）" maxLength={128} />
                </Form.Item>
              </div>
              <Form.Item name="secondary_base_url" label="副模型 API 接口地址" extra="留空按提供商取默认地址；自定义提供商必填">
                <Input placeholder="https://dashscope.aliyuncs.com/compatible-mode/v1" maxLength={512} />
              </Form.Item>
              <Form.Item
                name="secondary_api_key"
                label="副模型 API 密钥"
                extra={apiKeySet ? '已配置密钥：留空表示保持现有密钥不变，输入新值则覆盖' : '尚未配置副模型密钥，配置后共识断言生效'}
              >
                <Input.Password
                  placeholder={apiKeySet ? '••••••••（保持不变请留空）' : 'sk-...'}
                  autoComplete="new-password"
                  maxLength={512}
                />
              </Form.Item>
            </Card>

            {/* ============ 视频断言 ============ */}
            <Card size="small" style={cardStyle} title={<SectionTitle icon={<VideoCameraOutlined />} title="视频断言" hint="四期 C" />}>
              <Form.Item
                name="video_assertions_enabled"
                label="启用视频断言"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
                extra="开启后执行时录制全程视频，video=true 的断言按过程帧序列做多模态判定；接管执行模式录制不了视频，自动退回截图判定"
              >
                <Switch checkedChildren="开" unCheckedChildren="关" />
              </Form.Item>
            </Card>

            {/* ============ 一键提单 ============ */}
            <Card size="small" style={cardStyle} title={<SectionTitle icon={<FileDoneOutlined />} title="一键提单" hint="禅道 / Jira" />}>
              <div style={grid2}>
                <Form.Item name="ticket_provider" label="提单平台" extra="未选择表示不启用提单">
                  <Select
                    allowClear
                    placeholder="未启用"
                    options={[
                      { value: 'zentao', label: '禅道 ZenTao' },
                      { value: 'jira', label: 'Jira' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="ticket_project_key" label="项目 Key / 产品 ID" extra="Jira 填项目 Key（如 QA），禅道填产品 ID（数字）">
                  <Input placeholder="如 QA 或 3" maxLength={64} />
                </Form.Item>
              </div>
              <Form.Item name="ticket_base_url" label="平台 API 地址" extra="禅道如 https://zentao.example.com/api.php/v1；Jira 如 https://your.atlassian.net">
                <Input placeholder="https://..." maxLength={512} />
              </Form.Item>
              <Form.Item
                name="ticket_token"
                label="平台 API Token"
                extra={ticketTokenSet ? '已配置 Token：留空表示保持现有 Token 不变，输入新值则覆盖' : '尚未配置 Token；Jira 使用邮箱:Token 形式或 Bearer Token，禅道使用系统 Token'}
              >
                <Input.Password
                  placeholder={ticketTokenSet ? '••••••••（保持不变请留空）' : 'Token'}
                  autoComplete="new-password"
                  maxLength={512}
                />
              </Form.Item>
            </Card>

            {/* ============ 邮箱验证码 ============ */}
            <Card size="small" style={cardStyle} title={<SectionTitle icon={<MailOutlined />} title="邮箱验证码（IMAP）" hint="三期 E" />}>
              <div style={grid2}>
                <Form.Item name="imap_host" label="IMAP 服务器地址" extra="如 imap.qq.com / imap.exmail.qq.com">
                  <Input placeholder="imap.example.com" maxLength={255} />
                </Form.Item>
                <Form.Item name="imap_port" label="端口" extra="端口 993 通常开启 SSL">
                  <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="993" />
                </Form.Item>
              </div>
              <div style={grid2}>
                <Form.Item name="imap_user" label="邮箱账号">
                  <Input placeholder="test@example.com" maxLength={128} autoComplete="off" />
                </Form.Item>
                <Form.Item name="imap_ssl" label="SSL" valuePropName="checked">
                  <Switch checkedChildren="开" unCheckedChildren="关" />
                </Form.Item>
              </div>
              <Form.Item
                name="imap_password"
                label="邮箱密码/授权码"
                style={{ marginBottom: 0 }}
                extra={imapPasswordSet ? '已配置密码：留空表示保持现有密码不变，输入新值则覆盖' : '尚未配置密码，配置后"读取邮箱验证码"步骤可用'}
              >
                <Input.Password
                  placeholder={imapPasswordSet ? '••••••••（保持不变请留空）' : '邮箱密码或 IMAP 授权码'}
                  autoComplete="new-password"
                  maxLength={255}
                />
              </Form.Item>
            </Card>

            {/* ============ 定时执行通知（五期 #4） ============ */}
            <Card size="small" style={cardStyle} title={<SectionTitle icon={<BellOutlined />} title="定时执行通知" hint="五期 #4" />}>
              <Form.Item
                name="notify_recipients"
                label="通知收件人"
                extra="定时计划执行结束后发送结果邮件；多个邮箱用英文逗号分隔，如 a@x.com,b@x.com"
              >
                <Input.TextArea
                  placeholder="a@example.com,b@example.com"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  maxLength={1024}
                />
              </Form.Item>
              <div style={grid2}>
                <Form.Item name="smtp_host" label="SMTP 服务器地址" extra="如 smtp.qq.com / smtp.exmail.qq.com">
                  <Input placeholder="smtp.example.com" maxLength={255} />
                </Form.Item>
                <Form.Item name="smtp_port" label="端口" extra="465 通常为 SSL，587 为 STARTTLS">
                  <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="465" />
                </Form.Item>
              </div>
              <div style={grid2}>
                <Form.Item name="smtp_user" label="发件邮箱账号">
                  <Input placeholder="sender@example.com" maxLength={128} autoComplete="off" />
                </Form.Item>
                <Form.Item name="smtp_ssl" label="SSL/TLS" valuePropName="checked">
                  <Switch checkedChildren="开" unCheckedChildren="关" />
                </Form.Item>
              </div>
              <Form.Item
                name="smtp_password"
                label="SMTP 密码/授权码"
                extra={smtpPasswordSet ? '已配置密码：留空表示保持现有密码不变，输入新值则覆盖' : '尚未配置密码，配置后定时执行通知生效'}
              >
                <Input.Password
                  placeholder={smtpPasswordSet ? '••••••••（保持不变请留空）' : '邮箱密码或 SMTP 授权码'}
                  autoComplete="new-password"
                  maxLength={255}
                />
              </Form.Item>
              <div style={grid2}>
                <Form.Item name="site_url" label="平台访问地址" extra="用于通知邮件中的「查看报告」链接前缀">
                  <Input placeholder="http://localhost:5173" maxLength={255} />
                </Form.Item>
                <Form.Item
                  name="notify_on_success"
                  label="成功也通知"
                  valuePropName="checked"
                  extra="默认仅执行失败/错误时发送通知邮件"
                >
                  <Switch checkedChildren="开" unCheckedChildren="关" />
                </Form.Item>
              </div>
            </Card>
          </div>
        </Form>
      </div>
    </div>
  )
}
